import { CheckCircle2, Circle, AlertTriangle } from "lucide-react";

/**
 * Horizontal courier-style phase tracker.
 * phases: [{ id, name, days, start_date, end_date }]
 * projectDueDate: ISO date string of project.due_date
 * projectStatus: "Planned" | "Active" | "In Progress" | "On Hold" | "Completed" | ...
 */
export default function PhaseTracker({ phases = [], projectDueDate, projectStatus, compact = false, testid = "phase-tracker", emptyText = "No phases yet — pick a Service template or add phases in Edit." }) {
  if (!Array.isArray(phases) || phases.length === 0) {
    return (
      <div data-testid={`${testid}-empty`} className="text-xs text-neutral-500 font-mono-tight">
        {emptyText}
      </div>
    );
  }
  const today = new Date().toISOString().slice(0, 10);
  const total = phases.length;
  const isCompleted = projectStatus === "Completed";

  let activeIdx = phases.findIndex((p) => (!p.start_date || p.start_date <= today) && (!p.end_date || p.end_date >= today));
  if (activeIdx === -1) {
    const pastCount = phases.filter((p) => p.end_date && p.end_date < today).length;
    activeIdx = pastCount >= total ? total - 1 : pastCount;
  }
  const progressPct = isCompleted ? 100 : Math.round(((activeIdx + 1) / total) * 100);

  let daysBehind = 0;
  if (!isCompleted && projectDueDate && today > projectDueDate) {
    const ms = new Date(today) - new Date(projectDueDate);
    daysBehind = Math.max(0, Math.round(ms / (1000 * 60 * 60 * 24)));
  }
  const onTrack = daysBehind === 0;

  return (
    <div data-testid={testid} className={`bg-white border border-black/5 rounded-2xl ${compact ? "p-4" : "p-5"}`}>
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <div className="label-caps flex items-center gap-2">
          Phase Tracker
          <span className="font-mono-tight text-royal-800" data-testid={`${testid}-progress`}>{progressPct}%</span>
        </div>
        <div>
          {onTrack ? (
            <span data-testid={`${testid}-ontrack`} className="inline-flex items-center gap-1 text-[11px] font-mono-tight uppercase tracking-widest bg-[#E9F5EE] text-[#175E33] px-2 py-1 rounded-full">
              <CheckCircle2 size={12} /> On Track
            </span>
          ) : (
            <span data-testid={`${testid}-behind`} className="inline-flex items-center gap-1 text-[11px] font-mono-tight uppercase tracking-widest bg-[#FFE7DE] text-[#7A1A00] px-2 py-1 rounded-full">
              <AlertTriangle size={12} /> {daysBehind} day{daysBehind === 1 ? "" : "s"} behind
            </span>
          )}
        </div>
      </div>

      <div className="relative">
        {/* base rail */}
        <div className="absolute left-0 right-0 top-3 h-1 bg-neutral-200 rounded-full" />
        {/* progress fill */}
        <div
          className={`absolute left-0 top-3 h-1 rounded-full ${onTrack ? "bg-royal-700" : "bg-[#B02A00]"}`}
          style={{ width: `${progressPct}%` }}
          data-testid={`${testid}-fill`}
        />
        <ol className="relative grid" style={{ gridTemplateColumns: `repeat(${total}, minmax(0, 1fr))` }}>
          {phases.map((p, i) => {
            const done = i < activeIdx || isCompleted;
            const isActive = !isCompleted && i === activeIdx;
            return (
              <li key={p.id || i} className="flex flex-col items-center text-center px-1" data-testid={`${testid}-phase-${i}`}>
                <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center shrink-0 z-10 bg-white ${
                  done ? "border-royal-700 bg-royal-700 text-white" :
                  isActive ? (onTrack ? "border-royal-700 ring-4 ring-royal-700/20" : "border-[#B02A00] ring-4 ring-[#B02A00]/20") :
                  "border-neutral-300 text-neutral-400"
                }`}>
                  {done ? <CheckCircle2 size={14} /> : <Circle size={10} className="fill-current" />}
                </div>
                <div className={`mt-2 text-xs font-semibold leading-tight ${isActive ? "text-royal-800" : "text-ink"}`} title={p.name}>
                  {p.name}
                </div>
                {p.end_date && (
                  <div className="text-[10px] font-mono-tight text-ink/50 mt-0.5">
                    {p.start_date || "—"} → {p.end_date}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}
